/* eslint-disable complexity */
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { Gltf } from '../gltf/@types/Gltf';
import { gltfStructureToModels } from '../gltf/parser/gltfStructureToModels';
import { gltfToStructure } from '../gltf/parser/gltfToStructure';
import PanoMeasurementModel from '../models/standard/PanoMeasurementModel';
import { useLoginContext } from '../provider/LoginProvider';
import { useQueryParams } from '../provider/QueryParamsProvider';
import { SnackbarType, useSnackbar } from '../provider/SnackbarProvider';
import { useToolbarContext } from '../provider/ToolbarProvider';
import { fetchPlanData } from '../utils/apiUtils';
import { clearPendingExternalPlanSession } from '../utils/externalPlanDataStorage';
import filesApiService from '../utils/filesApiService';
import { importPlanJson } from '../utils/planJsonExportImportUtils';

interface PlanDebugModel {
  dataObjectId?: number;
  eventId?: number;
  id?: number;
  jsonVersion?: string;
  kojeId?: number;
  kojeLevel?: number;
  name?: string;
  region?: string;
  showroomId?: number;
}

const getPlanDebugSnapshot = (plan?: Partial<PlanDebugModel> | null) => ({
  dataObjectId: plan?.dataObjectId,
  eventId: plan?.eventId,
  floorplannerId: plan?.id,
  jsonVersion: plan?.jsonVersion,
  kojeId: plan?.kojeId,
  kojeLevel: plan?.kojeLevel,
  name: plan?.name,
  region: plan?.region,
  showroomId: plan?.showroomId,
});

interface UsePlanLoaderProps {
  building: any;
  setPlanModel: (plan: any) => void;
  setBuilding: (building: any, forceUpdate?: boolean) => void;
  setRoomCompleted: (completed: boolean) => void;
  initializeImportedBuildingHistory: (building: any, cameraPositionSprites?: any[]) => any;
  panoramaMeasurementActions: any;
  addMeasurement: (m: any) => void;
  addCameraPositionSprite: (m: any) => void;
  addHotspot: (h: any) => void;
  resetAnnotations: () => void;
  setShowImportNotSupportedModal: (open: boolean) => void;
  setShowFetchingInfoModal: React.Dispatch<React.SetStateAction<boolean>>;
  setIsPlanLoaderBusy: React.Dispatch<React.SetStateAction<boolean>>;
}

const waitForNextPaint = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });

export const usePlanLoader = ({
  building,
  setPlanModel,
  setBuilding,
  setRoomCompleted,
  initializeImportedBuildingHistory,
  panoramaMeasurementActions,
  addMeasurement,
  addCameraPositionSprite,
  addHotspot,
  resetAnnotations,
  setShowImportNotSupportedModal,
  setShowFetchingInfoModal,
  setIsPlanLoaderBusy,
}: UsePlanLoaderProps) => {
  const intl = useIntl();
  const [authLoading, setAuthLoading] = useState(true);

  const { data: planData, clearQueryParams } = useQueryParams();
  const { showSnackbar } = useSnackbar();
  const { setIsDrawingAllowed } = useToolbarContext();
  const { isAuthReady, isAuthenticated, token, openLoginModal, refreshAccessToken } = useLoginContext();
  const activePlanDataRef = useRef<string | undefined>(undefined);
  const loginStartedRef = useRef(false);

  useEffect(() => {
    const loadPlanData = async () => {
      setAuthLoading(true);
      let hasLoadedExternalPlan = false;

      if (!planData) {
        setAuthLoading(false);
        return;
      }

      if (!isAuthReady) {
        return;
      }

      if (!isAuthenticated) {
        if (!loginStartedRef.current) {
          loginStartedRef.current = true;
          void openLoginModal(undefined, { showMissingModuleNotice: false });
        }
        return;
      }

      loginStartedRef.current = false;

      let keycloakAuthToken = token;
      try {
        keycloakAuthToken = (await refreshAccessToken()) || token;
      } catch {
        keycloakAuthToken = undefined;
      }

      if (!keycloakAuthToken) {
        if (!loginStartedRef.current) {
          loginStartedRef.current = true;
          void openLoginModal(undefined, { showMissingModuleNotice: false });
        }
        return;
      }

      if (activePlanDataRef.current === planData) {
        return;
      }

      activePlanDataRef.current = planData;
      setIsPlanLoaderBusy(true);
      await waitForNextPaint();

      try {
        setIsDrawingAllowed(false);
        const { plan, authToken, floorplannerData } = await fetchPlanData(planData, building, keycloakAuthToken);
        console.warn('[PlanDebug] usePlanLoader fetched plan', {
          floorplanId: floorplannerData?.floorplanId,
          hasFloorplanJson: !!floorplannerData?.floorplan && Object.keys(floorplannerData.floorplan).length > 0,
          hasGltf: !!floorplannerData?.gltf && Object.keys(floorplannerData.gltf).length > 0,
          plan: getPlanDebugSnapshot(plan),
        });
        setPlanModel(plan);
        hasLoadedExternalPlan = true;

        await filesApiService.ensureAuthenticated();

        const { gltf, floorplanId, floorplan } = floorplannerData;

        if (!floorplanId || floorplanId === -1 || floorplanId === 0) {
          // No floorplan – try fetching panorama measurements.
          const newModels = (await panoramaMeasurementActions.fetchPanoramaMeasurements(plan, authToken)) as Array<PanoMeasurementModel>;

          if (newModels.length === 0) {
            setIsDrawingAllowed(true);
          } else if (newModels.length === 1) {
            await panoramaMeasurementActions.handleImportMeasurements(newModels, plan);
          } else if (newModels.length > 1) {
            panoramaMeasurementActions.setMeasurementModalOpen(true);
          }
          return;
        }

        if (floorplan && Object.keys(floorplan).length > 0) {
          // Loading new plan
          const {
            plan: importedPlan,
            building: importedBuilding,
            measurements,
            cameraPositionSprites,
            hotspots,
            nonFetchableEntities: nonFetchableObjects,
          } = await importPlanJson(JSON.stringify(floorplan));
          console.warn('[PlanDebug] usePlanLoader imported floorplan before metadata merge', {
            fetchedPlan: getPlanDebugSnapshot(plan),
            importedPlan: getPlanDebugSnapshot(importedPlan),
          });
          // Overwrite selected fields from the fetched data
          importedPlan.name = plan.name;
          importedPlan.id = plan.id;

          if (importedPlan.kojeId === null || importedPlan.kojeId === undefined || importedPlan.kojeId === -1) {
            importedPlan.kojeId = plan.kojeId;
          }
          if (importedPlan.kojeLevel === null || importedPlan.kojeLevel === undefined || importedPlan.kojeLevel === -1) {
            importedPlan.kojeLevel = plan.kojeLevel;
          }
          if (importedPlan.eventId === null || importedPlan.eventId === undefined || importedPlan.eventId === -1) {
            importedPlan.eventId = plan.eventId;
          }
          if (importedPlan.dataObjectId === null || importedPlan.dataObjectId === undefined || importedPlan.dataObjectId === -1) {
            importedPlan.dataObjectId = plan.dataObjectId;
          }
          if ((importedPlan as any).showroomId === null || (importedPlan as any).showroomId === undefined || (importedPlan as any).showroomId === -1) {
            (importedPlan as any).showroomId = (plan as any).showroomId;
          }

          console.warn('[PlanDebug] usePlanLoader imported floorplan after metadata merge', {
            importedPlan: getPlanDebugSnapshot(importedPlan),
          });

          setPlanModel(importedPlan);
          const snappedImportedBuilding = initializeImportedBuildingHistory(importedBuilding, cameraPositionSprites);
          setBuilding({
            ...snappedImportedBuilding,
            isSetByUndoRedo: true,
          }, true);
          resetAnnotations();
          measurements.forEach((m: any) => addMeasurement(m));
          cameraPositionSprites.forEach((sprite: any) => addCameraPositionSprite(sprite));
          hotspots.forEach((h: any) => addHotspot(h));
          setRoomCompleted(true);

          // Case: Some building objects could not be fetched
          if (nonFetchableObjects.length > 0) {
            setShowFetchingInfoModal(true);
          }
        } else if (gltf && Object.keys(gltf).length > 0) {
          // Loading old plan
          try {
            let gltfAsJSON: Gltf | undefined;
            if ('gltf' in gltf) {
              gltfAsJSON = gltf.gltf as Gltf;
            } else {
              gltfAsJSON = gltf as Gltf;
            }
            if (!gltfAsJSON) {
              setShowImportNotSupportedModal(true);
            }
            const gltfStructure = gltfToStructure(gltfAsJSON);
            const parsedModels = await gltfStructureToModels(gltfStructure);
            const snappedImportedBuilding = initializeImportedBuildingHistory(parsedModels.building);
            setBuilding({
              ...snappedImportedBuilding,
              isSetByUndoRedo: true,
            }, true);
            setRoomCompleted(true);
          } catch (e) {
            setShowImportNotSupportedModal(true);
          }
        } else {
          showSnackbar(intl.formatMessage({ id: 'PlanLoader.loadError' }), SnackbarType.ERROR);
        }
      } catch (error) {
        showSnackbar(intl.formatMessage({ id: 'PlanLoader.loadError' }), SnackbarType.ERROR);
      } finally {
        if (hasLoadedExternalPlan) {
          clearPendingExternalPlanSession();
        }
        clearQueryParams();
        activePlanDataRef.current = undefined;
        await waitForNextPaint();
        setAuthLoading(false);
        setIsPlanLoaderBusy(false);
      }
    };

    loadPlanData();
  }, [planData, isAuthReady, isAuthenticated, token, openLoginModal, refreshAccessToken, clearQueryParams]);

  return { authLoading };
};
